import React, { useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { AlertTriangle, Upload } from 'lucide-react';
import LogoCropModal from '../components/LogoCropModal';
import InvitationCard from '../components/InvitationCard';

const LogoSettingsPage = () => {
  const { user, setUser } = useAuth();
  const [logoUrl, setLogoUrl] = useState(user?.logoUrl || null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const res = await api.put('/profile/logo', { logoUrl });
      setUser?.(res.data);
      toast({ title: 'Organizer logo updated' });
    } catch (error) {
      toast({
        title: 'Failed to update logo',
        description: error?.error?.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold">Logo Settings</h1>
      </div>

      <Card className="p-6 space-y-4">
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            This logo will show up in the footer of your QR Invitation card as organizer logo.{' '}
            <button
              type="button"
              className="text-orange-600 underline font-medium"
              onClick={() => setPreviewOpen(true)}
            >
              Preview
            </button>
            .
          </span>
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
        <div
          className="border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 py-10 px-4 text-center cursor-pointer hover:border-blue-400 transition"
          onClick={() => fileInputRef.current?.click()}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Organizer logo" className="max-h-16 object-contain" />
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-400" />
              <p className="text-sm text-gray-500">Drag and drop a photo here or click to open file</p>
            </>
          )}
        </div>

        <div className="text-sm text-gray-600 space-y-1">
          <p>Best Image Dimensions: 240x50. Only JPG &amp; PNG allowed.</p>
          <p>
            Organizer Logo:{' '}
            <button
              type="button"
              className="text-orange-600 underline font-medium"
              onClick={() => setLogoUrl(null)}
            >
              Reset to default
            </button>
          </p>
        </div>

        <Button
          className="bg-orange-500 hover:bg-orange-600"
          onClick={handleUpdate}
          disabled={saving}
        >
          {saving ? 'Updating...' : 'Update'}
        </Button>
      </Card>

      <LogoCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        onCancel={() => {
          setCropOpen(false);
          setCropSrc(null);
        }}
        onConfirm={(dataUrl) => {
          setLogoUrl(dataUrl);
          setCropOpen(false);
          setCropSrc(null);
        }}
      />

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Preview Template</DialogTitle>
          </DialogHeader>
          <div className="w-56 aspect-[3/4] rounded-lg overflow-hidden border mx-auto">
            <InvitationCard
              template={{
                name: 'Corporate Conclave',
                category: 'Corporate',
                colorFrom: '#f97316',
                colorTo: '#c2410c',
                isPremium: false,
              }}
              data={{ organizerLogoUrl: logoUrl }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogoSettingsPage;
